import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import {
  FiBell,
  FiCalendar,
  FiCheckSquare,
  FiDollarSign,
  FiAlertCircle,
  FiMail,
  FiTrash2,
  FiCheck,
  FiArrowRight,
  FiAlertTriangle,
  FiX
} from 'react-icons/fi';
import {
  getUnreadCount,
  getNotifications,
  markAsRead,
  markAllAsRead,
  deleteNotification
} from '../../finance/api/notifications';
import { useAuth } from '../../finance/auth/AuthContext';
import { fmtDate } from '../../utils/istDate';

function formatRelativeTime(dateString) {
  if (!dateString) return '';
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now - date;
  const diffSec = Math.floor(diffMs / 1000);
  const diffMin = Math.floor(diffSec / 60);
  const diffHour = Math.floor(diffMin / 60);
  const diffDay = Math.floor(diffHour / 24);

  if (diffSec < 60) return 'Just now';
  if (diffMin < 60) return `${diffMin}m ago`;
  if (diffHour < 24) return `${diffHour}h ago`;
  if (diffDay === 1) return 'Yesterday';
  return fmtDate(date);
}

export default function NotificationDropdown() {
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef(null);

  // Close dropdown on outside click
  useEffect(() => {
    function handleClickOutside(event) {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Fetch unread count from API
  const { data: countData } = useQuery({
    queryKey: ['notifications', 'unread-count'],
    queryFn: getUnreadCount,
    enabled: isAuthenticated,
    refetchInterval: 30000,
    retry: false,
  });

  // Fetch latest notifications from API when open
  const { data: notificationsData, isLoading } = useQuery({
    queryKey: ['notifications', 'latest'],
    queryFn: () => getNotifications({ page: 0, size: 10 }),
    enabled: isAuthenticated && isOpen,
    retry: false,
  });

  const apiNotifications = notificationsData?.content || (Array.isArray(notificationsData) ? notificationsData : []);
  const notifications = apiNotifications;
  const unreadCount = countData?.unreadCount ?? 0;

  const handleMarkAsRead = async (id) => {
    try {
      await markAsRead(id);
      qc.invalidateQueries({ queryKey: ['notifications'] });
    } catch (e) {
      console.error('Failed to mark notification as read:', e);
    }
  };

  const handleMarkAllAsRead = async () => {
    try {
      await markAllAsRead();
      toast.success('All notifications marked as read');
      qc.invalidateQueries({ queryKey: ['notifications'] });
    } catch (e) {
      toast.error('Failed to mark all as read');
    }
  };

  const handleDelete = async (e, id) => {
    e.stopPropagation();
    try {
      await deleteNotification(id);
      toast.success('Notification deleted');
      qc.invalidateQueries({ queryKey: ['notifications'] });
    } catch (e) {
      toast.error('Failed to delete notification');
    }
  };

  const handleNotificationClick = (notif) => {
    setIsOpen(false);
    if (!notif.isRead) {
      handleMarkAsRead(notif.id);
    }
    if (notif.actionUrl) {
      let url = notif.actionUrl;
      if (url === '/TasksView') url = '/my-space/tasks';
      navigate(url);
    }
  };

  const getIcon = (type, title = '') => {
    const upperType = String(type || '').toUpperCase();
    const upperTitle = String(title || '').toUpperCase();
    
    if (upperTitle.includes('LOP') || upperTitle.includes('REVOKED') || upperTitle.includes('INSUFFICIENT')) {
      return <FiAlertTriangle className="text-amber-500" />;
    }
    if (upperTitle.includes('REJECTED')) {
      return <FiAlertCircle className="text-rose-500" />;
    }
    
    switch (upperType) {
      case 'LEAVE':
        return <FiCalendar className="text-amber-600" />;
      case 'TASK':
        return <FiCheckSquare className="text-blue-600" />;
      case 'FINANCE':
        return <FiDollarSign className="text-emerald-600" />;
      case 'SYSTEM':
        return <FiAlertCircle className="text-purple-600" />;
      default:
        return <FiMail className="text-slate-500" />;
    }
  };

  return (
    <div className="relative" ref={containerRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="relative p-2 rounded-full text-slate-500 hover:bg-slate-100 hover:text-blue-600 transition-all focus:outline-none cursor-pointer"
        title="Notifications"
        aria-label="Notifications"
      >
        <FiBell className="text-xl" />
        {unreadCount > 0 && (
          <span className="absolute top-0 right-0 flex h-4 w-4 items-center justify-center rounded-full bg-rose-500 text-[10px] font-bold text-white ring-2 ring-white">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {isOpen && (
        <div className="absolute right-0 mt-2.5 w-80 sm:w-96 rounded-2xl border border-slate-200 bg-white shadow-xl z-50 overflow-hidden animate-in fade-in zoom-in-95 duration-150">
          {/* Header with Exit Button */}
          <div className="flex items-center justify-between border-b border-slate-100 px-4 py-3 bg-slate-50/80">
            <div className="flex items-center gap-2">
              <span className="text-sm font-bold text-slate-800">Notifications</span>
              {unreadCount > 0 && (
                <span className="px-1.5 py-0.2 text-[10px] font-bold bg-blue-100 text-blue-700 rounded-full">
                  {unreadCount}
                </span>
              )}
            </div>
            <div className="flex items-center gap-2">
              {unreadCount > 0 && (
                <button
                  onClick={handleMarkAllAsRead}
                  className="flex items-center gap-1 text-xs font-semibold text-blue-600 hover:text-blue-700 transition-colors cursor-pointer"
                  title="Mark all as read"
                >
                  <FiCheck size={14} /> Mark all read
                </button>
              )}
              {/* Exit / Close button */}
              <button
                onClick={() => setIsOpen(false)}
                className="p-1 rounded-lg text-slate-400 hover:text-slate-700 hover:bg-slate-200/70 transition-colors cursor-pointer"
                title="Close"
                aria-label="Close"
              >
                <FiX size={16} />
              </button>
            </div>
          </div>

          {/* Notification List */}
          <div className="max-h-[360px] overflow-y-auto divide-y divide-slate-100">
            {isLoading ? (
              <div className="flex items-center justify-center py-10 px-4 text-center">
                <span className="text-xs text-slate-400 font-medium">Loading notifications...</span>
              </div>
            ) : notifications.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-10 px-4 text-center">
                <div className="w-12 h-12 bg-slate-100 rounded-full flex items-center justify-center text-slate-400 mb-2">
                  <FiBell size={20} />
                </div>
                <span className="text-sm font-bold text-slate-700">No notifications yet</span>
                <span className="text-xs text-slate-400 mt-1">We'll inform you when there are updates.</span>
              </div>
            ) : (
              notifications.map((notif) => (
                <div
                  key={notif.id}
                  className={`group relative flex items-start gap-3 p-3.5 hover:bg-slate-50 transition-colors cursor-pointer ${
                    !notif.isRead ? 'bg-blue-50/30' : ''
                  }`}
                  onClick={() => handleNotificationClick(notif)}
                >
                  {/* Type Icon */}
                  <div className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-xl bg-slate-100 text-sm">
                    {getIcon(notif.type, notif.title)}
                  </div>

                  {/* Body */}
                  <div className="flex-1 min-w-0 pr-5">
                    <div className="flex items-center gap-1.5">
                      <span className={`text-xs text-slate-800 truncate ${!notif.isRead ? 'font-bold' : 'font-semibold'}`}>
                        {notif.title}
                      </span>
                      {!notif.isRead && (
                        <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-blue-600" />
                      )}
                    </div>
                    <p className={`text-xs mt-0.5 text-slate-500 leading-relaxed line-clamp-2 ${!notif.isRead ? 'text-slate-700 font-medium' : ''}`}>
                      {notif.message}
                    </p>
                    <span className="text-[10px] text-slate-400 mt-1 block">
                      {formatRelativeTime(notif.createdAt)}
                    </span>
                  </div>

                  {/* Delete button */}
                  <button
                    onClick={(e) => handleDelete(e, notif.id)}
                    className="absolute right-2.5 top-3 p-1 rounded-lg text-slate-300 hover:text-rose-600 hover:bg-rose-50 transition-all opacity-0 group-hover:opacity-100 cursor-pointer"
                    title="Delete notification"
                  >
                    <FiTrash2 size={13} />
                  </button>
                </div>
              ))
            )}
          </div>

          {/* Footer */}
          <div className="p-2.5 border-t border-slate-100 bg-slate-50/80 text-center">
            <button
              onClick={() => {
                setIsOpen(false);
                navigate('/notifications');
              }}
              className="inline-flex items-center gap-1.5 text-xs font-bold text-blue-600 hover:text-blue-700 transition-colors cursor-pointer"
            >
              View All Notifications <FiArrowRight size={13} />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
